import type { DbExecutor } from "./executor";
import { generateId } from "./ids";
import { ValidationError } from "./repositories/projectRepository";
import type { AttachmentRepository } from "./repositories/attachmentRepository";
import type { CatalogRepository } from "./repositories/catalogRepository";
import type { PurchaseRepository } from "./repositories/purchaseRepository";
import type { LaborSettlementRepository } from "./repositories/laborSettlementRepository";
import type { Attachment, AttachmentKind } from "../domain/attachment";
import type { CostType } from "../domain/proposedCostLine";
import type { QuantityUnit } from "../domain/feature";
import type { Cents } from "../domain/money";
import type { RefundMethod } from "../domain/purchase";
import { computePurchase } from "../engine/purchaseMath";
import { rankPossibleDuplicates, rankWorkSuggestions, type DuplicateEvidence } from "../documents/receiptSuggestions";
import {
  InterpreterError,
  canPreviewMime,
  unsupportedDocumentMessage,
  type DocumentInterpreter,
  type DocumentKind,
  type ExtractedDocument,
} from "../documents/interpreter";
import { looksLikeReturn } from "../documents/azureDocumentIntelligence";
import { proposeLoanTerms, type LoanTermsProposal } from "../documents/loanTerms";
import { suggestProduct, type ProductCandidate } from "../documents/matching";
import { checkDocumentArithmetic, receiptImportTotals, type ReconciliationCheck } from "../engine/documentImport";
import { companyReceiptShippingOverrides } from "../engine/companyReceipt96";
import {parseStructuredDocumentText,type DocumentEvidence97} from "../documents/structuredTextDocument";

/**
 * Receipt & document intelligence (Phase 9D, spec §33A).
 *
 * THE PERMANENT RULE: interpretation PROPOSES; the existing Purchase →
 * Purchase Line → Allocation records remain accounting truth. Nothing in
 * this service writes an accounting record until the user confirms, and
 * confirmation goes through the SAME Phase 5 repositories everything else
 * uses — there is no receipt ledger.
 *
 * Extraction is stored once with full provenance (page, polygon,
 * confidence, raw value) so the source region can be highlighted later
 * without ever re-running — and re-running is never needed to view a cost.
 */

export type ExtractionStatus = "needs-review" | "ready" | "imported" | "failed";

export interface ExtractionLineRecord {
  id: string;
  lineIndex: number;
  extractedDescription: string;
  extractedSku: string;
  extractedUpc: string;
  extractedQtyX100: number | null;
  extractedUnit: string | null;
  extractedUnitPriceCents: Cents | null;
  extractedAmountCents: Cents | null;
  confidenceX100: number | null;
  confirmedDescription: string | null;
  confirmedQtyX100: number | null;
  confirmedUnit: string | null;
  confirmedAmountCents: Cents | null;
  costType: CostType | null;
  taxable: boolean | null;
  suggestedProductId: string | null;
  suggestedFeatureId: string | null;
  suggestionBasis: string;
  suggestionStrength: string;
  confirmedProductId: string | null;
  /** The expense category the reviewer chose, or null for the default. */
  confirmedCategoryId: string | null;
  confirmedFeatureId: string | null;
  confirmedProjectId: string | null;
  confirmedProposedLineId: string | null;
  disposition: "include" | "ignore";
  purchaseLineId: string | null;
  /** Effective values (confirmed wins; extracted otherwise). */
  description: string;
  quantityX100: number | null;
  amountCents: Cents | null;
  /** True when the user changed what the model said. */
  corrected: boolean;
}

export interface ExtractionRecord {
  evidence:DocumentEvidence97|null;
  id: string;
  attachmentId: string;
  projectId: string | null;
  documentKind: DocumentKind;
  status: ExtractionStatus;
  providerId: string;
  modelId: string;
  apiVersion: string;
  extractedAt: string;
  pageCount: number;
  vendor: string;
  documentDate: string | null;
  receiptNumber: string;
  subtotalCents: Cents | null;
  discountCents: Cents | null;
  taxCents: Cents | null;
  shippingCents: Cents | null;
  totalCents: Cents | null;
  paymentMethod: string;
  reconciles: boolean;
  reconcileNote: string;
  importedPurchaseId: string | null;
  importedAt: string | null;
  errorMessage: string;
  notes: string;
  lines: ExtractionLineRecord[];
  attachment: Attachment | null;
}

export interface RegionRecord {
  id: string;
  lineId: string | null;
  fieldName: string;
  pageNumber: number;
  polygon: number[];
  pageWidth: number | null;
  pageHeight: number | null;
  pageUnit: string;
  rawValue: string;
}

export interface ImportOutcome {
  purchaseId: string;
  lineCount: number;
  allocationCount: number;
  ignoredCount: number;
}
export interface ReceiptImportOptions {
  /** Explicit owner confirmation; an order-details PDF does not establish actual payment. */
  actualSpendingConfirmed?:boolean;
  business?: BusinessReceiptAnnotation;
  originalPurchaseId?: string | null;
  refundMethod?: RefundMethod | null;
  distinctReason?: string;
  obligationMatch?: { commitmentId:string; sourceDate:string; settles:boolean; allocationLineId?:string };
  coveredLaborLineIds?: string[];
  coveredWorkLineIds?: string[];
  fullySettlesCoveredWork?: boolean;
}
export interface BusinessReceiptAnnotation {
  businessCategoryId:string;
  classificationStatus?:"needs-review"|"confirmed";
  accountReference?:string;
  notes?:string;
  overheadConfirmed:boolean;
  asset?:{description:string;serialReference?:string;notes?:string};
}
export interface ReceiptSplit { projectId:string; amountCents:number; categoryId:string|null; featureId:string|null; notes:string }
const importQueues=new WeakMap<DbExecutor,Promise<unknown>>();

export class DocumentIntelligenceService {
  private confirmHook: ((extractionId:string,projectId:string|null,options:ReceiptImportOptions)=>Promise<ImportOutcome>) | null = null;
  private businessImportHook: ((purchaseId:string,input:BusinessReceiptAnnotation)=>Promise<unknown>) | null = null;
  setBusinessImportHook(hook:NonNullable<DocumentIntelligenceService["businessImportHook"]>):void { this.businessImportHook=hook; }
  private postImportHook: ((purchaseId:string,projectId:string,options:ReceiptImportOptions)=>Promise<void>) | null = null;
  /** Hosted owner route executes this whole operation server-side; never call inside a client transaction. */
  setConfirmHook(hook:NonNullable<DocumentIntelligenceService["confirmHook"]>):void { this.confirmHook=hook; }
  setPostImportHook(hook:NonNullable<DocumentIntelligenceService["postImportHook"]>):void { this.postImportHook=hook; }
  constructor(
    private readonly db: DbExecutor,
    private readonly interpreter: DocumentInterpreter,
    private readonly attachments: AttachmentRepository,
    private readonly catalog: CatalogRepository,
    private readonly purchases: PurchaseRepository,
    private readonly settlements: LaborSettlementRepository,
  ) {}

  async isConfigured(): Promise<{ configured: boolean; path: string; note: string | null }> {
    return this.interpreter.credentialStatus();
  }

  /* ---------------- upload + interpret ---------------- */

  private async readLocalFirst(input:{bytes:Uint8Array;filename:string;mimeType:string;kind:DocumentKind}):Promise<ExtractedDocument>{
    if(input.mimeType.toLowerCase()==="application/pdf"){
      const embedded=await (await import("../documents/localPdfText")).readEmbeddedPdfText(input.bytes);
      if(embedded)return parseStructuredDocumentText(embedded.text,input.kind,embedded.pageCount);
    }
    return this.interpreter.interpret(input);
  }

  /**
   * Import a document: store the original through the EXISTING managed
   * attachment system (never discarded), detect duplicates by SHA-256,
   * then interpret it into a reviewable candidate.
   */
  async ingest(input: {
    bytes: Uint8Array;
    filename: string;
    mimeType: string;
    kind: DocumentKind;
    projectId: string | null;
    attachmentKind?: AttachmentKind;
    /** Retain evidence for manual review without invoking an external reader. */
    manualOnly?: boolean;
  }): Promise<{ extraction: ExtractionRecord; duplicateOf: ExtractionRecord | null }> {
    const unsupported = unsupportedDocumentMessage(input.mimeType, input.filename);
    if (unsupported && !canPreviewMime(input.mimeType) && !this.interpreter.supportedMimeTypes.includes(input.mimeType)) {
      throw new ValidationError([unsupported]);
    }

    const existing = await this.attachments.findBySha256(await sha256(input.bytes));
    if (existing) {
      const prior = await this.findByAttachment(existing.id);
      if (prior) {
        // Same bytes already imported — never silently duplicate the money.
        return { extraction: prior, duplicateOf: prior };
      }
    }

    const imported = await this.attachments.importBytes(
      input.bytes,
      input.filename,
      input.attachmentKind ?? (input.kind === "invoice" ? "invoice" : input.kind === "return-receipt" ? "return-receipt" : "receipt"),
    );

    const now = new Date().toISOString();
    const id = generateId();
    let doc: ExtractedDocument | null = null;
    let errorMessage = "";
    try {
      if(input.manualOnly)errorMessage="Original retained for manual review. Automatic reading was not requested.";
      else {
        doc = await this.readLocalFirst({
        bytes: input.bytes,
        filename: input.filename,
        mimeType: input.mimeType,
        kind: input.kind,
        });
      }
    } catch (err) {
      errorMessage = err instanceof InterpreterError ? err.message : "The document could not be read.";
    }

    if (!doc) {
      await this.db.execute(
        `INSERT INTO document_extractions (id, attachment_id, project_id, document_kind, status, provider_id,
           model_id, api_version, extracted_at, page_count, error_message, created_at, updated_at)
         VALUES (?, ?, ?, ?, 'failed', ?, '', '', ?, 0, ?, ?, ?)`,
        [id, imported.attachment.id, input.projectId, input.kind, input.manualOnly?"manual":this.interpreter.id, now, errorMessage, now, now],
      );
      if(input.manualOnly)await this.db.execute("UPDATE document_extractions SET status='needs-review' WHERE id=?",[id]);
      const failed = await this.getById(id);
      return { extraction: failed!, duplicateOf: null };
    }

    // A return document that arrived as a "receipt" is reclassified so the
    // reviewer sees the right workflow — ambiguity asks, never guesses.
    const kind: DocumentKind = input.kind === "receipt" && looksLikeReturn(doc) ? "return-receipt" : input.kind;

    const lineTotal = doc.lines.reduce((s, l) => s + (l.amountCents.value ?? 0), 0);
    const check = checkDocumentArithmetic({
      lineTotalCents: lineTotal,
      subtotalCents: doc.subtotalCents.value,
      discountCents: doc.discountCents.value,
      taxCents: doc.taxCents.value,
      shippingCents: doc.shippingCents.value,
      totalCents: doc.totalCents.value,
    });

    await this.db.transaction(async () => {
      await this.db.execute(
        `INSERT INTO document_extractions (id, attachment_id, project_id, document_kind, status, provider_id,
           model_id, api_version, extracted_at, page_count, vendor, document_date, receipt_number,
           subtotal_cents, discount_cents, tax_cents, shipping_cents, total_cents, payment_method,
           reconciles, reconcile_note, created_at, updated_at,source_evidence_json)
         VALUES (?, ?, ?, ?, 'needs-review', ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          id, imported.attachment.id, input.projectId, kind, doc!.providerId, doc!.modelId, doc!.apiVersion,
          now, doc!.pageCount, doc!.vendor.value ?? "", doc!.documentDate.value, doc!.receiptNumber.value ?? "",
          doc!.subtotalCents.value, doc!.discountCents.value, doc!.taxCents.value, doc!.shippingCents.value,
          doc!.totalCents.value, doc!.paymentMethod.value ?? "",
          check.ok ? 1 : 0, check.problems.join(" "), now, now,JSON.stringify(doc!.evidence??{}),
        ],
      );
      // Header field regions (Total, MerchantName…) for highlighting.
      const headerFields: Array<[string, typeof doc.vendor]> = [
        ["Vendor", doc!.vendor], ["Date", doc!.documentDate], ["ReceiptNumber", doc!.receiptNumber],
        ["Subtotal", doc!.subtotalCents as never], ["Tax", doc!.taxCents as never],
        ["Shipping", doc!.shippingCents as never], ["Total", doc!.totalCents as never],
      ];
      for (const [name, field] of headerFields) {
        if (!field?.region) continue;
        await this.insertRegion(id, null, name, field.region, field.raw, now);
      }
      for (let i = 0; i < doc!.lines.length; i++) {
        const line = doc!.lines[i];
        const lineId = generateId();
        await this.db.execute(
          `INSERT INTO extraction_lines (id, extraction_id, line_index, extracted_description, extracted_sku,
             extracted_upc, extracted_qty_x100, extracted_unit, extracted_unit_price_cents, extracted_amount_cents,
             confidence_x100, cost_type, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            lineId, id, i, line.description.value ?? line.description.raw, line.sku.value ?? "",
            line.upc.value ?? "", line.quantityX100.value, line.unit.value, line.unitPriceCents.value,
            line.amountCents.value, line.confidence === null ? null : Math.round(line.confidence * 100),
            line.costType, now, now,
          ],
        );
        if (line.region) await this.insertRegion(id, lineId, "line", line.region, line.description.raw, now);
        if (line.amountCents.region) await this.insertRegion(id, lineId, "amount", line.amountCents.region, line.amountCents.raw, now);
      }
    });

    if (input.projectId) await this.suggestMatches(id, input.projectId);
    await this.revalidate(id);
    const extraction = await this.getById(id);
    return { extraction: extraction!, duplicateOf: null };
  }

  private async insertRegion(
    extractionId: string,
    lineId: string | null,
    fieldName: string,
    region: { pageNumber: number; polygon: number[]; pageWidth: number | null; pageHeight: number | null; pageUnit: string },
    rawValue: string,
    now: string,
  ): Promise<void> {
    await this.db.execute(
      `INSERT INTO extraction_regions (id, extraction_id, line_id, field_name, page_number, polygon,
         page_width, page_height, page_unit, raw_value, created_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [generateId(), extractionId, lineId, fieldName, region.pageNumber, JSON.stringify(region.polygon),
       region.pageWidth, region.pageHeight, region.pageUnit, rawValue, now],
    );
  }

  /* ---------------- matching ---------------- */

  /** Product candidates with project context (BOM/selection awareness). */
  private async candidates(projectId: string): Promise<ProductCandidate[]> {
    const products = await this.catalog.listProducts();
    const rows = await this.db.select<{ product_id: string; feature_id: string }>(
      `SELECT s.product_id, f.id AS feature_id
         FROM project_product_selections s
         JOIN assembly_instance_components c ON c.id = s.component_id
         JOIN assembly_instances i ON i.id = c.instance_id
         JOIN project_features f ON f.id = i.project_feature_id
        WHERE f.project_id = ? AND s.replaced_at IS NULL`,
      [projectId],
    );
    const byProduct = new Map<string, string[]>();
    for (const r of rows) byProduct.set(r.product_id, [...(byProduct.get(r.product_id) ?? []), r.feature_id]);
    const upcRows = await this.db.select<{ product_id: string; value: string }>(
      "SELECT product_id, value FROM product_attributes WHERE lower(key) = 'upc'",
    );
    const upcs = new Map(upcRows.map((r) => [r.product_id, r.value]));
    const external = await this.db.select<{ id: string; external_product_id: string | null }>(
      "SELECT id, external_product_id FROM products",
    );
    const externalById = new Map(external.map((r) => [r.id, r.external_product_id]));
    return products
      .filter((p) => p.status !== "archived")
      .map((p) => ({
        id: p.id,
        name: p.name,
        brand: p.brand,
        sku: p.sku,
        materialDefinitionId: p.materialDefinitionId,
        externalProductId: externalById.get(p.id) ?? null,
        upc: upcs.get(p.id) ?? null,
        selectedInProject: byProduct.has(p.id),
        featureIds: byProduct.get(p.id) ?? [],
      }));
  }

  /** Compute suggestions for every line. Suggestions only — never confirms. */
  async suggestMatches(extractionId: string, projectId: string): Promise<void> {
    const candidates = await this.candidates(projectId);
    const lines = await this.db.select<{ id: string; extracted_description: string; extracted_sku: string; extracted_upc: string }>(
      "SELECT id, extracted_description, extracted_sku, extracted_upc FROM extraction_lines WHERE extraction_id = ?",
      [extractionId],
    );
    for (const line of lines) {
      const suggestion = suggestProduct(
        {
          description: line.extracted_description,
          sku: line.extracted_sku,
          upc: line.extracted_upc,
          externalProductId: null,
        },
        candidates,
      );
      await this.db.execute(
        `UPDATE extraction_lines SET suggested_product_id = ?, suggested_feature_id = ?, suggestion_basis = ?,
           suggestion_strength = ?, updated_at = ? WHERE id = ?`,
        [suggestion.productId, suggestion.featureId, suggestion.basis, suggestion.strength, new Date().toISOString(), line.id],
      );
    }
    await this.db.execute("UPDATE document_extractions SET project_id = ?, updated_at = ? WHERE id = ?", [
      projectId, new Date().toISOString(), extractionId,
    ]);
  }

  /* ---------------- review edits ---------------- */

  private async editable(extractionId:string):Promise<ExtractionRecord> {
    const record=await this.getById(extractionId);
    if(!record)throw new ValidationError(["That extraction no longer exists."]);
    if(record.importedPurchaseId)throw new ValidationError(["Imported document reviews are frozen. Use the ordinary correction or return workflow."]);
    return record;
  }

  /** Enter an explicitly described whole invoice or missing line on the retained original. */
  async addManualLine(extractionId:string,description:string,amountCents:number):Promise<void> {
    await this.editable(extractionId);
    if(!description.trim()||!Number.isSafeInteger(amountCents))throw new ValidationError(["Enter a description and an amount in whole cents."]);
    const rows=await this.db.select<{n:number}>("SELECT COALESCE(MAX(line_index),-1)+1 AS n FROM extraction_lines WHERE extraction_id=?",[extractionId]);
    const now=new Date().toISOString();
    await this.db.execute("INSERT INTO extraction_lines (id,extraction_id,line_index,extracted_description,confirmed_description,confirmed_amount_cents,created_at,updated_at) VALUES (?,?,?,?,?,?,?,?)",[generateId(),extractionId,rows[0].n,"",description.trim(),amountCents,now,now]);
    await this.db.execute("UPDATE document_extractions SET status='needs-review', updated_at=? WHERE id=?",[now,extractionId]);
    await this.revalidate(extractionId);
  }

  async lineSplits(lineId:string):Promise<ReceiptSplit[]> {
    return this.db.select<ReceiptSplit>("SELECT project_id AS projectId, amount_cents AS amountCents, category_id AS categoryId, feature_id AS featureId, notes FROM receipt_line_splits WHERE line_id=? ORDER BY id",[lineId]);
  }
  async setLineSplits(lineId:string,splits:ReceiptSplit[]):Promise<void> {
    const rows=await this.db.select<{extraction_id:string}>("SELECT extraction_id FROM extraction_lines WHERE id=?",[lineId]);
    if(!rows.length)throw new ValidationError(["That document line no longer exists."]);
    await this.editable(rows[0].extraction_id);
    if(splits.length>30||splits.some(row=>!row.projectId||!Number.isSafeInteger(row.amountCents)))throw new ValidationError(["Choose projects and exact whole-cent amounts for up to 30 splits."]);
    await this.db.transaction(async()=>{
      await this.db.execute("DELETE FROM receipt_line_splits WHERE line_id=?",[lineId]);
      for(const row of splits)await this.db.execute("INSERT INTO receipt_line_splits(id,line_id,project_id,amount_cents,category_id,feature_id,notes) VALUES(?,?,?,?,?,?,?)",[generateId(),lineId,row.projectId,row.amountCents,row.categoryId,row.featureId,row.notes]);
    });
  }

  async possibleDuplicates(extractionId:string,projectId:string|null) {
    const record=await this.getById(extractionId);if(!record)return [];
    if(projectId!==null&&!(await this.db.select("SELECT id FROM projects WHERE id=?",[projectId])).length)throw new ValidationError(["That project is unavailable."]);
    // Search only this executor's authenticated owner workspace, across its
    // projects. The project receiving a new photo may be the wrong one.
    const date=record.documentDate??"",timestamp=Date.parse(date+"T00:00:00Z");
    const from=Number.isFinite(timestamp)?new Date(timestamp-14*86400000).toISOString().slice(0,10):"";
    const to=Number.isFinite(timestamp)?new Date(timestamp+14*86400000).toISOString().slice(0,10):"";
    const vendor=record.vendor.toLowerCase().replace(/[^a-z0-9]/g,"");
    const normalizedVendorSql=(column:string)=>[" ","-",".",",","'","&","/","(",")","_"].reduce((expression,character)=>"replace("+expression+", '"+character.replaceAll("'","''")+"', '')","lower("+column+")");
    const eligible=(alias:string,dateColumn:string)=>"("+dateColumn+" BETWEEN ? AND ? OR (?<>'' AND "+normalizedVendorSql(alias+".vendor")+"=?))";
    const bounds=[from,to,vendor,vendor];
    const purchases=await this.db.select<DuplicateEvidence>(`SELECT p.id,(SELECT a.project_id FROM purchase_allocations a JOIN purchase_lines l ON l.id=a.line_id WHERE l.purchase_id=p.id ORDER BY a.created_at,a.id LIMIT 1) AS projectId,'purchase' AS source,p.vendor,p.date,p.total_cents AS totalCents,p.receipt_number AS invoiceNumber,p.notes||' '||COALESCE((SELECT group_concat(pl.description,' ') FROM purchase_lines pl WHERE pl.purchase_id=p.id),'') AS description FROM purchases p WHERE p.voided_at IS NULL AND ${eligible("p","p.date")} ORDER BY p.date DESC,p.id LIMIT 250`,bounds);
    const expenses=await this.db.select<DuplicateEvidence>(`SELECT e.id,e.project_id AS projectId,'expense' AS source,e.vendor,e.date,e.amount_cents+e.tax_cents AS totalCents,COALESCE(s.invoice_number,'') AS invoiceNumber,e.description FROM actual_expenses e LEFT JOIN spending_submissions s ON s.expense_id=e.id WHERE e.voided_at IS NULL AND ${eligible("e","e.date")} ORDER BY e.date DESC,e.id LIMIT 250`,bounds);
    const documents=await this.db.select<DuplicateEvidence>(`SELECT e.id,e.project_id AS projectId,'document' AS source,e.vendor,e.document_date AS date,e.total_cents AS totalCents,e.receipt_number AS invoiceNumber,e.notes AS description FROM document_extractions e WHERE e.id<>? AND e.imported_purchase_id IS NULL AND ${eligible("e","e.document_date")} ORDER BY e.created_at DESC,e.id LIMIT 250`,[extractionId,...bounds]);
    return rankPossibleDuplicates({vendor:record.vendor,date:record.documentDate,totalCents:record.documentKind==="return-receipt"&&record.totalCents!==null?-Math.abs(record.totalCents):record.totalCents,invoiceNumber:record.receiptNumber,description:record.lines.map(row=>row.description).join(" ")},[...purchases,...expenses,...documents]);
  }

  async workSuggestions(extractionId:string,projectId:string) {
    const record=await this.getById(extractionId);if(!record)return [];
    const features=await this.db.select<{id:string;name:string;categoryId:string|null;roomName:string;unit:string|null}>("SELECT f.id,f.name,(SELECT category_id FROM proposed_cost_lines WHERE feature_id=f.id AND archived_at IS NULL ORDER BY created_at LIMIT 1) AS categoryId,COALESCE(r.name,'') AS roomName,f.quantity_unit AS unit FROM project_features f LEFT JOIN rooms r ON r.id=f.room_id WHERE f.project_id=? AND f.archived_at IS NULL",[projectId]);
    const prior=await this.db.select<{description:string;featureId:string}>("SELECT COALESCE(l.confirmed_description,l.extracted_description) AS description,l.confirmed_feature_id AS featureId FROM extraction_lines l JOIN document_extractions e ON e.id=l.extraction_id WHERE e.project_id=? AND e.status='imported' AND l.confirmed_feature_id IS NOT NULL",[projectId]);
    return record.lines.map(line=>({lineId:line.id,candidates:rankWorkSuggestions(line.description,features.map(feature=>({...feature,selectedProduct:feature.id===line.suggestedFeatureId&&line.suggestionStrength==="exact",priorConfirmed:prior.some(row=>row.featureId===feature.id&&row.description?.trim().toLowerCase()===line.description.trim().toLowerCase())})),line.confirmedUnit??line.extractedUnit)}));
  }

  /** Record the reviewer's correction WITHOUT erasing what the model said. */
  async updateLine(
    lineId: string,
    patch: Partial<{
      confirmedDescription: string | null;
      confirmedQtyX100: number | null;
      confirmedUnit: string | null;
      confirmedAmountCents: Cents | null;
      costType: CostType | null;
      taxable: boolean | null;
      confirmedProductId: string | null;
      confirmedFeatureId: string | null;
      confirmedProjectId: string | null;
      confirmedProposedLineId: string | null;
      /** Spec §60N: which KIND of cost it is, even when the work item is unknown. */
      confirmedCategoryId: string | null;
      disposition: "include" | "ignore";
    }>,
  ): Promise<void> {
    const owners=await this.db.select<{extraction_id:string}>("SELECT extraction_id FROM extraction_lines WHERE id=?",[lineId]);
    if(!owners.length)throw new ValidationError(["That document line no longer exists."]);
    await this.editable(owners[0].extraction_id);
    const sets: string[] = [];
    const values: unknown[] = [];
    const map: Record<string, string> = {
      confirmedDescription: "confirmed_description",
      confirmedQtyX100: "confirmed_qty_x100",
      confirmedUnit: "confirmed_unit",
      confirmedAmountCents: "confirmed_amount_cents",
      costType: "cost_type",
      confirmedProductId: "confirmed_product_id",
      confirmedFeatureId: "confirmed_feature_id",
      confirmedProjectId: "confirmed_project_id",
      confirmedProposedLineId: "confirmed_proposed_line_id",
      confirmedCategoryId: "confirmed_category_id",
      disposition: "disposition",
    };
    for (const [key, column] of Object.entries(map)) {
      if (!(key in patch)) continue;
      sets.push(`${column} = ?`);
      values.push((patch as Record<string, unknown>)[key]);
    }
    if ("taxable" in patch) {
      sets.push("taxable = ?");
      values.push(patch.taxable === null || patch.taxable === undefined ? null : patch.taxable ? 1 : 0);
    }
    if (sets.length === 0) return;
    sets.push("updated_at = ?");
    values.push(new Date().toISOString(), lineId);
    await this.db.execute(`UPDATE extraction_lines SET ${sets.join(", ")} WHERE id = ?`, values);
    await this.revalidate(owners[0].extraction_id);
  }

  /** Correct a header value the model misread. */
  async updateHeader(
    extractionId: string,
    patch: Partial<{
      vendor: string; documentDate: string | null; receiptNumber: string; documentKind: DocumentKind;paymentMethod:string;
      subtotalCents: Cents | null; discountCents: Cents | null; taxCents: Cents | null;
      shippingCents: Cents | null; totalCents: Cents | null; projectId: string | null; notes: string;
    }>,
  ): Promise<void> {
    const before=await this.editable(extractionId);
    await this.db.execute("INSERT INTO receipt_review_events(id,extraction_id,event_json,created_at) VALUES(?,?,?,?)",[generateId(),extractionId,JSON.stringify({kind:"header-correction",before:Object.fromEntries(Object.keys(patch).map(key=>[key,before[key as keyof ExtractionRecord]])),patch}),new Date().toISOString()]);
    const map: Record<string, string> = {
      vendor: "vendor", documentDate: "document_date", receiptNumber: "receipt_number",
      documentKind: "document_kind", subtotalCents: "subtotal_cents", discountCents: "discount_cents",
      taxCents: "tax_cents", shippingCents: "shipping_cents", totalCents: "total_cents",
      projectId: "project_id", notes: "notes",paymentMethod:"payment_method",
    };
    const sets: string[] = [];
    const values: unknown[] = [];
    for (const [key, column] of Object.entries(map)) {
      if (!(key in patch)) continue;
      sets.push(`${column} = ?`);
      values.push((patch as Record<string, unknown>)[key]);
    }
    if (sets.length === 0) return;
    sets.push("updated_at = ?");
    values.push(new Date().toISOString(), extractionId);
    await this.db.execute(`UPDATE document_extractions SET ${sets.join(", ")} WHERE id = ?`, values);
    await this.revalidate(extractionId);
  }

  /** Re-run the deterministic arithmetic over current (corrected) values. */
  async revalidate(extractionId: string): Promise<ReconciliationCheck> {
    const record = await this.getById(extractionId);
    if (!record) throw new ValidationError(["That extraction no longer exists."]);
    const lineTotal = record.lines
      .filter((l) => l.disposition === "include")
      .reduce((s, l) => s + (l.amountCents ?? 0), 0);
    let check = checkDocumentArithmetic({
      lineTotalCents: lineTotal,
      subtotalCents: record.subtotalCents,
      discountCents: record.discountCents,
      taxCents: record.taxCents,
      shippingCents: record.shippingCents,
      totalCents: record.totalCents,
    });
    try {
      const included=record.lines.filter(line=>line.disposition==="include");
      if(included.some(line=>line.amountCents===null))throw Error("Each included line needs an amount.");
      const totals=receiptImportTotals(included.map(line=>line.amountCents!),record,record.documentKind==="return-receipt");
      check={ok:true,problems:[],computedTotalCents:totals.total,totalDifferenceCents:0,subtotalDifferenceCents:record.subtotalCents===null?null:0};
    } catch(error) {check={...check,ok:false,problems:[(error as Error).message]};}
    await this.db.execute(
      "UPDATE document_extractions SET reconciles = ?, reconcile_note = ?, updated_at = ? WHERE id = ?",
      [check.ok ? 1 : 0, check.problems.join(" "), new Date().toISOString(), extractionId],
    );
    return check;
  }

  /* ---------------- import ---------------- */

  /**
   * Confirm the review: create the EXISTING Phase 5 records. One Purchase
   * (or return), its lines, and allocations to whichever project each line
   * belongs to — a receipt may legitimately serve several projects, and the
   * Purchase is never duplicated to achieve that.
   */
  async confirmImport(extractionId:string,defaultProjectId:string|null,options:ReceiptImportOptions={}):Promise<ImportOutcome> {
    if(this.confirmHook)return this.confirmHook(extractionId,defaultProjectId,options);
    // Independent confirmations share one executor; never let reentrant
    // transaction composition turn two concurrent clicks into one transaction.
    const prior=importQueues.get(this.db)??Promise.resolve();
    const work=prior.catch(()=>undefined).then(()=>this.importReviewed(extractionId,defaultProjectId,options));
    importQueues.set(this.db,work.catch(()=>undefined));
    return work;
  }

  private async importReviewed(extractionId:string,defaultProjectId:string|null,options:ReceiptImportOptions):Promise<ImportOutcome> {
    return this.db.transaction(async()=>{
      const record=await this.getById(extractionId);
      if(!record)throw new ValidationError(["That extraction no longer exists."]);
      if(record.importedPurchaseId)throw new ValidationError(["This document has already been imported."]);
      if(record.evidence?.documentType==="order-details"&&options.actualSpendingConfirmed!==true)throw new ValidationError(["An order-details document does not prove payment. Independently confirm actual spending before importing this order as Actual."]);
      if(record.projectId!==null&&record.projectId!==defaultProjectId)throw new ValidationError(["Open this document in its own project before importing."]);
      if(defaultProjectId===null&&(!options.business||!this.businessImportHook))throw new ValidationError(["Review the business classification and confirm its overhead before importing without a project."]);
      if(defaultProjectId!==null&&options.business)throw new ValidationError(["Open a shared business receipt in Business Finances before choosing company overhead."]);
      if(defaultProjectId===null&&(options.obligationMatch||options.coveredLaborLineIds?.length||options.coveredWorkLineIds?.length))throw new ValidationError(["Import the shared cost once, then confirm each exact bill or covered-work match from its source."]);
      const included=record.lines.filter(line=>line.disposition==="include");
      if(!included.length)throw new ValidationError(["Every line is ignored — there is nothing to import. Add a manual invoice amount on this retained document."]);
      for(const line of included)if(line.amountCents===null)throw new ValidationError(["Each included line needs a confirmed amount."]);
      const isReturn=record.documentKind==="return-receipt";
      if(options.coveredLaborLineIds?.length&&options.coveredWorkLineIds?.length)throw new ValidationError(["Choose labor grouping or mixed covered work, not both."]);
      if(options.coveredWorkLineIds?.length){
        const ids=options.coveredWorkLineIds;
        if(ids.length>200||new Set(ids).size!==ids.length||typeof options.fullySettlesCoveredWork!=="boolean")throw new ValidationError(["Choose distinct covered work and confirm whether this financially settles it."]);
        const owned=await this.db.select("SELECT id FROM proposed_cost_lines WHERE project_id=? AND archived_at IS NULL AND id IN ("+ids.map(()=>"?").join(",")+")",[defaultProjectId,...ids]);
        if(owned.length!==ids.length)throw new ValidationError(["Choose current covered work in this project."]);
      }
      if(isReturn&&!["original-payment","cash","store-credit","exchange","unknown","other"].includes(options.refundMethod??""))throw new ValidationError(["Choose the refund method explicitly. Select Unknown when no cash refund is confirmed."]);
      if(isReturn&&!options.originalPurchaseId)throw new ValidationError(["Choose which purchase this return reverses."]);
      let totals:ReturnType<typeof receiptImportTotals>;
      try{totals=receiptImportTotals(included.map(line=>line.amountCents!),record,isReturn);}catch(error){throw new ValidationError([(error as Error).message]);}
      const duplicates=await this.possibleDuplicates(extractionId,defaultProjectId);
      if(duplicates.length&&(!options.distinctReason||options.distinctReason.trim().length<5))throw new ValidationError(["Possible duplicate: open the existing record or explain why this is a distinct purchase before importing."]);
      if(!record.attachment?.sha256)throw new ValidationError(["The original document identity is missing. Reopen the retained original before importing."]);
      const prior=await this.db.select<{id:string}>("SELECT e.id FROM document_extractions e JOIN attachments a ON a.id=e.attachment_id WHERE a.sha256 = ? AND e.imported_purchase_id IS NOT NULL",[record.attachment.sha256]);
      if(prior.length)throw new ValidationError(["This document attachment has already been imported."]);
      const now=new Date().toISOString();
      await this.db.execute("INSERT INTO receipt_import_claims (extraction_id,attachment_id,document_sha256,review_json,created_at) VALUES (?,?,?,?,?)",[record.id,record.attachmentId,record.attachment.sha256,JSON.stringify({totalCents:record.totalCents,totals,refundMethod:options.refundMethod??null,distinctReason:options.distinctReason??null,obligationMatch:options.obligationMatch??null,coveredLaborLineIds:options.coveredLaborLineIds??[],coveredWorkLineIds:options.coveredWorkLineIds??[],fullySettlesCoveredWork:options.fullySettlesCoveredWork??false,duplicateIds:duplicates.map(d=>({source:d.source,id:d.id}))}),now]);
      const sign=isReturn?-1:1;
      const purchase=await this.purchases.createPurchase({kind:isReturn?"return":"purchase",originalPurchaseId:options.originalPurchaseId??null,vendor:record.vendor,date:record.documentDate??new Date().toISOString().slice(0,10),receiptNumber:record.receiptNumber,subtotalCents:sign*totals.merchandise,discountCents:sign*totals.discount,taxCents:sign*totals.tax,shippingCents:sign*totals.shipping,totalCents:sign*totals.total,paymentMethod:record.paymentMethod,refundMethod:isReturn?options.refundMethod!:null,notes:"Reviewed document import; source extraction "+record.id});
      const createdLines=[];
      for(const line of included){
        const created=await this.purchases.addLine(purchase.id,{description:line.description,quantityX100:line.quantityX100===null?null:sign*Math.abs(line.quantityX100),unit:(line.confirmedUnit??line.extractedUnit??null) as QuantityUnit|null,sku:line.extractedSku,grossCents:sign*Math.abs(line.amountCents!),lineDiscountCents:0,taxable:line.taxable??totals.tax!==0,explicitTaxCents:null,productId:line.confirmedProductId??null,categoryId:line.confirmedCategoryId??(line.costType==="labor"?"labor":line.costType==="material"?"materials":null),costType:line.costType,notes:""});
        createdLines.push({line,created});
        await this.db.execute("UPDATE extraction_lines SET purchase_line_id = ?, updated_at = ? WHERE id = ?",[created.id,now,line.id]);
      }
      const graph=await this.purchases.getGraph(purchase.id);
      const calculation=computePurchase(graph!);
      let allocationCount=0;
      for(const {line,created} of createdLines){
        const available=calculation.lines.find(row=>row.lineId===created.id)!.netCents;
        const splits=await this.lineSplits(line.id);
        if(splits.length&&defaultProjectId!==null&&splits.reduce((sum,row)=>sum+row.amountCents,0)!==available)throw new ValidationError(["Split allocations must add exactly to the line's net amount after purchase discount."]);
        const assignments=splits.length?splits:[{projectId:line.confirmedProjectId??defaultProjectId,amountCents:available,categoryId:null,featureId:line.confirmedFeatureId,notes:"Imported from receipt"}];
        for(const split of assignments){
          if(split.projectId===null)continue;
          await this.purchases.addAllocation(created.id,{projectId:split.projectId,amountCents:split.amountCents,quantityX100:splits.length?null:created.quantityX100,shippingOverrideCents:defaultProjectId===null?0:null,categoryOverrideId:split.categoryId,bucketOverride:null,linkedProposedLineId:splits.length?null:line.confirmedProposedLineId,linkedFeatureId:!splits.length&&line.confirmedProposedLineId?null:split.featureId,notes:split.notes});allocationCount++;
        }
      }
      if(defaultProjectId===null){
        // Project shares and explicit overhead participate in the same
        // shipping distribution. Never push all shipping onto the projects.
        const reviewed=(await this.purchases.getGraph(purchase.id))!;
        for(const portion of companyReceiptShippingOverrides(reviewed))await this.purchases.updateAllocation(portion.id,{...reviewed.allocations.find(a=>a.id===portion.id)!,shippingOverrideCents:portion.shippingCents});
        await this.businessImportHook!(purchase.id,options.business!);
      }
      const final=computePurchase((await this.purchases.getGraph(purchase.id))!);
      if(defaultProjectId!==null&&final.allocations.reduce((sum,row)=>sum+row.totalCents,0)!==purchase.totalCents)throw new ValidationError(["The allocations, tax and shipping do not reconcile to the document total. Correct the taxable lines before importing."]);
      if(options.coveredLaborLineIds?.length){
        if(included.length!==1||included[0].costType!=="labor"||final.allocations.length!==1||final.allocations[0].projectId!==defaultProjectId||isReturn)throw new ValidationError(["Grouped labor coverage needs one explicitly classified labor line and one project allocation. Mixed invoices require a documented split or later review."]);
        await this.settlements.create(defaultProjectId!,{description:record.vendor+" invoice — owner-confirmed covered labor",allocationId:final.allocations[0].allocationId,proposedLineIds:options.coveredLaborLineIds,notes:"Confirmed during document import; no per-item actual cost inferred."});
      }
      if(options.coveredWorkLineIds?.length&&options.fullySettlesCoveredWork===true){
        if(included.length!==1||included[0].costType!==null||final.allocations.length!==1||final.allocations[0].projectId!==defaultProjectId||isReturn)throw new ValidationError(["Mixed covered work needs one unclassified whole-invoice line and one project allocation. Do not invent a material/labor split."]);
        await this.settlements.createMixed(defaultProjectId!,{description:record.vendor+" invoice — owner-confirmed covered work",allocationId:final.allocations[0].allocationId,proposedLineIds:options.coveredWorkLineIds,confirmedFullSettlement:true,notes:"Explicit financial settlement during receipt review; not a completion certificate and no per-item actual amounts."});
      }
      if(options.obligationMatch&&!this.postImportHook)throw new ValidationError(["Record this cost without a bill match, then match the exact payment from Bills."]);
      if(this.postImportHook&&defaultProjectId!==null)await this.postImportHook(purchase.id,defaultProjectId,options);
      await this.attachments.link(record.attachmentId,{type:"purchase",id:purchase.id});
      if(record.evidence?.documentType==="order-details")await this.db.execute("INSERT INTO receipt_review_events(id,extraction_id,event_json,created_at) VALUES(?,?,?,?)",[generateId(),record.id,JSON.stringify({kind:"order-actual-confirmation",actualSpendingConfirmed:true,purchaseId:purchase.id,sourceDoesNotProveDelivery:true}),now]);
      await this.db.execute("UPDATE document_extractions SET status='imported', imported_purchase_id=?, imported_at=?, updated_at=? WHERE id=?",[purchase.id,now,now,record.id]);
      await this.db.execute("UPDATE receipt_import_claims SET purchase_id=? WHERE extraction_id=?",[purchase.id,record.id]);
      return {purchaseId:purchase.id,lineCount:included.length,allocationCount,ignoredCount:record.lines.length-included.length};
    });
  }

  /**
   * Settle an invoice's labor lines as ONE grouped labor payment
   * (spec §31A): the invoice is the source document, the group is the
   * comparison unit, and no per-item labor actual is invented.
   */
  async createLaborSettlementFromImport(input: {
    extractionId: string;
    projectId: string;
    description: string;
    proposedLineIds: string[];
  }): Promise<{ settlementId: string }> {
    const record = await this.getById(input.extractionId);
    if (!record?.importedPurchaseId) {
      throw new ValidationError(["Import the invoice first, then group its labor."]);
    }
    const laborLine = record.lines.find((l) => l.costType === "labor" && l.purchaseLineId);
    if (!laborLine) throw new ValidationError(["This invoice has no imported labor line to settle."]);
    const allocations = await this.db.select<{ id: string }>(
      "SELECT id FROM purchase_allocations WHERE line_id = ? ORDER BY created_at LIMIT 1",
      [laborLine.purchaseLineId],
    );
    if (allocations.length === 0) throw new ValidationError(["That labor line has no allocation to settle."]);
    const settlement = await this.settlements.create(input.projectId, {
      description: input.description || `${record.vendor} invoice`,
      allocationId: allocations[0].id,
      proposedLineIds: input.proposedLineIds,
    });
    return { settlementId: settlement.id };
  }

  /* ---------------- reads ---------------- */

  async listInbox(projectId: string | null): Promise<ExtractionRecord[]> {
    const rows = await this.db.select<{ id: string }>(
      projectId
        ? "SELECT id FROM document_extractions WHERE project_id = ? OR project_id IS NULL ORDER BY created_at DESC"
        : "SELECT id FROM document_extractions ORDER BY created_at DESC",
      projectId ? [projectId] : [],
    );
    const out: ExtractionRecord[] = [];
    for (const row of rows) {
      const record = await this.getById(row.id);
      if (record) out.push(record);
    }
    return out;
  }

  async findByAttachment(attachmentId: string): Promise<ExtractionRecord | null> {
    const rows = await this.db.select<{ id: string }>(
      "SELECT id FROM document_extractions WHERE attachment_id = ? ORDER BY created_at DESC LIMIT 1",
      [attachmentId],
    );
    return rows.length > 0 ? this.getById(rows[0].id) : null;
  }

  /** The extraction that produced a purchase (provenance → source document). */
  async findByPurchase(purchaseId: string): Promise<ExtractionRecord | null> {
    const rows = await this.db.select<{ id: string }>(
      "SELECT id FROM document_extractions WHERE imported_purchase_id = ? LIMIT 1",
      [purchaseId],
    );
    return rows.length > 0 ? this.getById(rows[0].id) : null;
  }

  /** Regions for highlighting; optionally narrowed to one purchase line. */
  async regionsFor(extractionId: string, purchaseLineId?: string | null): Promise<RegionRecord[]> {
    const rows = await this.db.select<{
      id: string; line_id: string | null; field_name: string; page_number: number; polygon: string;
      page_width: number | null; page_height: number | null; page_unit: string; raw_value: string;
    }>(
      purchaseLineId
        ? `SELECT r.* FROM extraction_regions r
             JOIN extraction_lines l ON l.id = r.line_id
            WHERE r.extraction_id = ? AND l.purchase_line_id = ?`
        : "SELECT * FROM extraction_regions WHERE extraction_id = ?",
      purchaseLineId ? [extractionId, purchaseLineId] : [extractionId],
    );
    return rows.map((r) => ({
      id: r.id,
      lineId: r.line_id,
      fieldName: r.field_name,
      pageNumber: Number(r.page_number),
      polygon: safeJson(r.polygon),
      pageWidth: r.page_width === null ? null : Number(r.page_width),
      pageHeight: r.page_height === null ? null : Number(r.page_height),
      pageUnit: r.page_unit,
      rawValue: r.raw_value,
    }));
  }

  async getById(id: string): Promise<ExtractionRecord | null> {
    const rows = await this.db.select<Record<string, unknown>>(
      "SELECT * FROM document_extractions WHERE id = ?",
      [id],
    );
    if (rows.length === 0) return null;
    const r = rows[0];
    const lineRows = await this.db.select<Record<string, unknown>>(
      "SELECT * FROM extraction_lines WHERE extraction_id = ? ORDER BY line_index",
      [id],
    );
    const attachment = await this.attachments.get(String(r.attachment_id));
    return {
      evidence:parseEvidence97(r.source_evidence_json),
      id: String(r.id),
      attachmentId: String(r.attachment_id),
      projectId: (r.project_id as string | null) ?? null,
      documentKind: String(r.document_kind) as DocumentKind,
      status: String(r.status) as ExtractionStatus,
      providerId: String(r.provider_id),
      modelId: String(r.model_id),
      apiVersion: String(r.api_version),
      extractedAt: String(r.extracted_at),
      pageCount: Number(r.page_count ?? 0),
      vendor: String(r.vendor ?? ""),
      documentDate: (r.document_date as string | null) ?? null,
      receiptNumber: String(r.receipt_number ?? ""),
      subtotalCents: numOrNull(r.subtotal_cents),
      discountCents: numOrNull(r.discount_cents),
      taxCents: numOrNull(r.tax_cents),
      shippingCents: numOrNull(r.shipping_cents),
      totalCents: numOrNull(r.total_cents),
      paymentMethod: String(r.payment_method ?? ""),
      reconciles: Number(r.reconciles) === 1,
      reconcileNote: String(r.reconcile_note ?? ""),
      importedPurchaseId: (r.imported_purchase_id as string | null) ?? null,
      importedAt: (r.imported_at as string | null) ?? null,
      errorMessage: String(r.error_message ?? ""),
      notes: String(r.notes ?? ""),
      attachment,
      lines: lineRows.map((l) => {
        const confirmedDescription = (l.confirmed_description as string | null) ?? null;
        const confirmedQty = numOrNull(l.confirmed_qty_x100);
        const confirmedAmount = numOrNull(l.confirmed_amount_cents);
        const extractedDescription = String(l.extracted_description ?? "");
        const extractedQty = numOrNull(l.extracted_qty_x100);
        const extractedAmount = numOrNull(l.extracted_amount_cents);
        return {
          id: String(l.id),
          lineIndex: Number(l.line_index),
          extractedDescription,
          extractedSku: String(l.extracted_sku ?? ""),
          extractedUpc: String(l.extracted_upc ?? ""),
          extractedQtyX100: extractedQty,
          extractedUnit: (l.extracted_unit as string | null) ?? null,
          extractedUnitPriceCents: numOrNull(l.extracted_unit_price_cents),
          extractedAmountCents: extractedAmount,
          confidenceX100: numOrNull(l.confidence_x100),
          confirmedDescription,
          confirmedQtyX100: confirmedQty,
          confirmedUnit: (l.confirmed_unit as string | null) ?? null,
          confirmedAmountCents: confirmedAmount,
          costType: (l.cost_type as CostType | null) ?? null,
          taxable: l.taxable === null || l.taxable === undefined ? null : Number(l.taxable) === 1,
          suggestedProductId: (l.suggested_product_id as string | null) ?? null,
          suggestedFeatureId: (l.suggested_feature_id as string | null) ?? null,
          suggestionBasis: String(l.suggestion_basis ?? ""),
          suggestionStrength: String(l.suggestion_strength ?? ""),
          confirmedProductId: (l.confirmed_product_id as string | null) ?? null,
          confirmedCategoryId: (l.confirmed_category_id as string | null) ?? null,
          confirmedFeatureId: (l.confirmed_feature_id as string | null) ?? null,
          confirmedProjectId: (l.confirmed_project_id as string | null) ?? null,
          confirmedProposedLineId: (l.confirmed_proposed_line_id as string | null) ?? null,
          disposition: (String(l.disposition ?? "include") as "include" | "ignore"),
          purchaseLineId: (l.purchase_line_id as string | null) ?? null,
          description: confirmedDescription ?? extractedDescription,
          quantityX100: confirmedQty ?? extractedQty,
          amountCents: confirmedAmount ?? extractedAmount,
          corrected:
            (confirmedDescription !== null && confirmedDescription !== extractedDescription) ||
            (confirmedQty !== null && confirmedQty !== extractedQty) ||
            (confirmedAmount !== null && confirmedAmount !== extractedAmount),
        };
      }),
    };
  }

  /** Remove a candidate that was never imported (inbox cleanup). */
  async discard(extractionId: string): Promise<void> {
    const record = await this.getById(extractionId);
    if (!record) return;
    if (record.importedPurchaseId) {
      throw new ValidationError(["This document was already imported. Void the purchase in Accounting instead."]);
    }
    await this.db.transaction(async () => {
      await this.db.execute("DELETE FROM extraction_regions WHERE extraction_id = ?", [extractionId]);
      await this.db.execute("DELETE FROM extraction_lines WHERE extraction_id = ?", [extractionId]);
      await this.db.execute("DELETE FROM document_extractions WHERE id = ?", [extractionId]);
    });
    // The stored document itself is kept unless nothing references it.
    const links = await this.attachments.linkCount(record.attachmentId);
    if (links === 0) await this.attachments.archive(record.attachmentId);
  }

  /**
   * READ A RETURNED CONTRACTOR QUOTE (spec §69-7/9/10).
   *
   * The same 9D rule in a lighter frame: AI PROPOSES, the person confirms.
   * A quote is not accounting — no Purchase, no extraction rows — so what
   * this returns is only a PREFILL for the ordinary quote form: the vendor
   * name, the bottom-line total and the date the provider read, with the
   * uploaded file imported as an ordinary attachment to link to the quote
   * the user then records. A document the provider cannot read is still a
   * perfectly good quote — the person types the number, as they always
   * could, and the file is still attached.
   */
  async readQuoteProposal(input: { bytes: Uint8Array; filename: string; mimeType: string }): Promise<{
    attachmentId: string;
    contractor: string | null;
    amountCents: number | null;
    quotedOn: string | null;
    note: string;
    requiresArithmeticReview?:boolean;
  }> {
    const imported = await this.attachments.importBytes(input.bytes, input.filename, "other");
    try {
      const doc = await this.readLocalFirst({
        bytes: input.bytes,
        filename: input.filename,
        mimeType: input.mimeType,
        kind: "invoice",
      });
      const total = doc.totalCents.value ?? doc.subtotalCents.value ?? null;
      const check=checkDocumentArithmetic({lineTotalCents:doc.lines.reduce((sum,line)=>sum+(line.amountCents.value??0),0),subtotalCents:doc.subtotalCents.value,discountCents:doc.discountCents.value,taxCents:doc.taxCents.value,shippingCents:doc.shippingCents.value,totalCents:doc.totalCents.value});
      return {
        attachmentId: imported.attachment.id,
        contractor: doc.vendor.value,
        amountCents: total,
        quotedOn: doc.documentDate.value,
        requiresArithmeticReview:!check.ok,
        note:
          !check.ok ? `Amounts need review: ${check.problems.join(" ")}` : total === null
            ? "The document was read but no total was found — enter the price from the paper."
            : "Read from the document — verify against the paper before recording.",
      };
    } catch (err) {
      return {
        attachmentId: imported.attachment.id,
        contractor: null,
        amountCents: null,
        quotedOn: null,
        note: err instanceof InterpreterError ? err.message : "The document could not be read. Enter the quote by hand — the file is attached either way.",
      };
    }
  }

  /**
   * READ A LOAN AGREEMENT (spec §73D).
   *
   * The 9D rule, again: AI PROPOSES, the person confirms. The file imports
   * as an ordinary attachment linked to its facility; the provider's layout
   * model returns the document's TEXT; the pure proposer in
   * src/documents/loanTerms.ts turns that into suggested terms, each with
   * the snippet it came from. What returns here only PREFILLS the facility
   * form — nothing is written to the facility until the person saves it,
   * and an unreadable document still attaches. The raw proposal is kept in
   * financing_extractions beside what the user later confirms; the
   * document's contents never enter an audit row.
   */
  async readLoanAgreement(input: {
    projectId: string;
    /**
     * Null when the loan is being CREATED from the document: the caller
     * links the attachment to the facility once the person confirms and
     * saves it (an unsaved form has no facility id yet).
     */
    facilityId: string | null;
    bytes: Uint8Array;
    filename: string;
    mimeType: string;
  }): Promise<{ attachmentId: string; proposal: LoanTermsProposal | null; note: string }> {
    const imported = await this.attachments.importBytes(input.bytes, input.filename, "other");
    if (input.facilityId !== null) {
      await this.attachments.link(imported.attachment.id, { type: "financing", id: input.facilityId });
    }
    if (!this.interpreter.readText) {
      return {
        attachmentId: imported.attachment.id,
        proposal: null,
        note: "This build cannot read documents — the agreement is attached; enter the terms from the paper.",
      };
    }
    try {
      const text = await this.interpreter.readText({
        bytes: input.bytes,
        filename: input.filename,
        mimeType: input.mimeType,
      });
      const proposal = proposeLoanTerms(text);
      await this.db.execute(
        "INSERT INTO financing_extractions (id, project_id, attachment_id, proposed_json, created_at) VALUES (?, ?, ?, ?, ?)",
        [generateId(), input.projectId, imported.attachment.id, JSON.stringify(proposal), new Date().toISOString()],
      );
      return {
        attachmentId: imported.attachment.id,
        proposal,
        note: "Proposed from the document — every value shows the words it came from. Verify against the agreement before saving.",
      };
    } catch (err) {
      return {
        attachmentId: imported.attachment.id,
        proposal: null,
        note:
          err instanceof InterpreterError
            ? err.message
            : "The document could not be read. Enter the terms from the paper — the file is attached either way.",
      };
    }
  }
}

const numOrNull = (v: unknown): number | null => (v === null || v === undefined ? null : Number(v));
const parseEvidence97=(raw:unknown):DocumentEvidence97|null=>{
  try{const value=JSON.parse(String(raw??"{}")) as DocumentEvidence97;return ["order-details","receipt","invoice","return","unclassified"].includes(value.documentType)&&Array.isArray(value.warnings)?value:null;}catch{return null;}
};
const safeJson = (s: string): number[] => {
  try {
    const parsed = JSON.parse(s);
    return Array.isArray(parsed) ? parsed.filter((n): n is number => typeof n === "number") : [];
  } catch {
    return [];
  }
};

/** SHA-256 via WebCrypto (same digest the attachment store uses). */
async function sha256(bytes: Uint8Array): Promise<string> {
  const buffer = await crypto.subtle.digest("SHA-256", bytes as unknown as ArrayBuffer);
  return [...new Uint8Array(buffer)].map((b) => b.toString(16).padStart(2, "0")).join("");
}
